import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, RefreshCw, AlertTriangle, AlertCircle, Info, 
  TrendingUp, Filter, Search, ExternalLink, Eye
} from "lucide-react";
import { toast } from "sonner";
import { formatInTimezone } from "../utils/timezone";

const CoachLint = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userTimezone = user?.timezone || "UTC";
  
  // State
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    coaching_mode: "",
    coach_version: "",
    severity: "",
    pattern: "",
    session_id: ""
  });
  const [activeFilters, setActiveFilters] = useState({});
  
  // Tab state
  const [activeTab, setActiveTab] = useState("logs"); // "logs" or "stats"

  // Check admin access
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", "100");
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await authAxios.get(`/admin/plog/lint/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error("Error fetching lint logs:", error);
      toast.error("Failed to load lint logs");
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await authAxios.get("/admin/plog/lint/stats?days=7");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching lint stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [activeFilters]);

  const applyFilters = () => {
    setActiveFilters({ ...filters });
  };

  const clearFilters = () => {
    setFilters({
      coaching_mode: "",
      coach_version: "",
      severity: "",
      pattern: "",
      session_id: ""
    });
    setActiveFilters({});
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "high": return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case "warn": return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case "info": return <Info className="w-4 h-4 text-blue-400" />;
      default: return <Info className="w-4 h-4 text-white/40" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      high: "bg-red-500/20 text-red-400 border-red-500/30",
      warn: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      none: "bg-white/10 text-white/50 border-white/20"
    };
    return colors[severity] || colors.none;
  };

  const getModeBadge = (mode) => {
    return mode === "LIVE" 
      ? "bg-green-500/20 text-green-400 border-green-500/30"
      : "bg-purple-500/20 text-purple-400 border-purple-500/30";
  };

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-divine-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/plog")}
              className="text-white/60 hover:text-white"
              data-testid="back-to-plog"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-divine-gold" />
              <h1 className="font-cinzel text-xl text-white">COACH LINT</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={activeTab === "logs" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("logs")}
              className={activeTab === "logs" ? "bg-divine-gold text-black" : "text-white/60"}
            >
              Logs
            </Button>
            <Button
              variant={activeTab === "stats" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("stats")}
              className={activeTab === "stats" ? "bg-divine-gold text-black" : "text-white/60"}
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              Stats
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { fetchLogs(); fetchStats(); }}
              className="text-white/60 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === "logs" ? (
          <>
            {/* Filters */}
            <Card className="bg-white/5 border-white/10 mb-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <select
                    value={filters.coaching_mode}
                    onChange={(e) => setFilters(f => ({ ...f, coaching_mode: e.target.value }))}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="filter-coaching-mode"
                  >
                    <option value="">All Modes</option>
                    <option value="LIVE">LIVE</option>
                    <option value="SIMULATION">SIMULATION</option>
                  </select>
                  
                  <select
                    value={filters.coach_version}
                    onChange={(e) => setFilters(f => ({ ...f, coach_version: e.target.value }))}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="filter-coach-version"
                  >
                    <option value="">All Versions</option>
                    <option value="v1">V1</option>
                    <option value="v2">V2</option>
                  </select>
                  
                  <select
                    value={filters.severity}
                    onChange={(e) => setFilters(f => ({ ...f, severity: e.target.value }))}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="filter-severity"
                  >
                    <option value="">All Severities</option>
                    <option value="high">High</option>
                    <option value="warn">Warn</option>
                    <option value="info">Info</option>
                  </select>
                  
                  <select
                    value={filters.pattern}
                    onChange={(e) => setFilters(f => ({ ...f, pattern: e.target.value }))}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="filter-pattern"
                  >
                    <option value="">All Patterns</option>
                    <option value="mind_read_feels">mind_read_feels</option>
                    <option value="mind_read_thinks">mind_read_thinks</option>
                    <option value="mind_read_wants">mind_read_wants</option>
                    <option value="intent_claim">intent_claim</option>
                    <option value="means_claim">means_claim</option>
                    <option value="future_certainty">future_certainty</option>
                    <option value="certainty_adverbs">certainty_adverbs</option>
                  </select>
                  
                  <Input
                    placeholder="Session ID..."
                    value={filters.session_id}
                    onChange={(e) => setFilters(f => ({ ...f, session_id: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
                    data-testid="filter-session-id"
                  />
                </div>
                
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={applyFilters}
                    className="bg-divine-gold text-black hover:bg-divine-gold/90"
                    data-testid="apply-filters-btn"
                  >
                    Apply Filters
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-white/60 hover:text-white"
                    data-testid="clear-filters-btn"
                  >
                    Clear
                  </Button>
                  {Object.values(activeFilters).some(v => v) && (
                    <span className="text-white/40 text-xs ml-2">
                      {logs.length} results
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Logs List */}
            <div className="space-y-3">
              {logs.length === 0 ? (
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="py-12 text-center">
                    <Eye className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/40">No lint logs found</p>
                    <p className="text-white/30 text-sm mt-1">
                      Lint logs are created when V2 coach annotations contain mind-reading language
                    </p>
                  </CardContent>
                </Card>
              ) : (
                logs.map((log, index) => (
                  <Card 
                    key={log.log_id || index} 
                    className="bg-white/5 border-white/10 hover:bg-white/[0.07] transition-colors"
                    data-testid={`lint-log-${index}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: Severity + Mode + Version */}
                        <div className="flex items-center gap-2">
                          {getSeverityIcon(log.severity)}
                          <span className={`px-2 py-0.5 rounded text-xs border ${getSeverityBadge(log.severity)}`}>
                            {log.severity || "none"}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs border ${getModeBadge(log.coaching_mode)}`}>
                            {log.coaching_mode}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-white/60 border border-white/20">
                            {log.coach_version}
                          </span>
                        </div>
                        
                        {/* Right: Timestamp + Link */}
                        <div className="flex items-center gap-3">
                          <span className="text-white/40 text-xs">
                            {log.created_at ? formatInTimezone(log.created_at, userTimezone) : "Unknown"}
                          </span>
                          <Link
                            to={`/plog?session=${log.session_id}`}
                            className="text-divine-gold/60 hover:text-divine-gold"
                            title="View session"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                      
                      {/* Hit count + Patterns */}
                      <div className="mt-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium">
                            {log.lint?.hit_count || 0} hits
                          </span>
                          {log.lint?.pattern_counts && Object.entries(log.lint.pattern_counts).map(([pattern, count]) => (
                            <span 
                              key={pattern}
                              className="px-2 py-0.5 rounded text-xs bg-red-500/10 text-red-300 border border-red-500/20"
                            >
                              {pattern}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Excerpts (if present) */}
                      {log.lint?.hits && log.lint.hits.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {log.lint.hits.map((hit, hitIndex) => (
                            <div 
                              key={hitIndex}
                              className="bg-black/30 rounded-lg p-3 border border-white/5"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-white/50 text-xs uppercase">{hit.field}</span>
                                <span className="text-red-400 text-xs">{hit.pattern}</span>
                              </div>
                              <p className="text-white/70 text-sm font-mono">
                                {hit.excerpt}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Context */}
                      <div className="mt-3 flex items-center gap-4 text-xs text-white/30">
                        <span>Level: {log.context?.effective_level || "?"}</span>
                        <span>Framing: {log.output_framing}</span>
                        <span className="font-mono truncate max-w-[200px]" title={log.session_id}>
                          Session: {log.session_id?.slice(0, 20)}...
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        ) : (
          /* Stats Tab */
          <div className="space-y-6">
            {loadingStats ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-divine-gold animate-spin" />
              </div>
            ) : stats ? (
              <>
                {/* Mode Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {["LIVE", "SIMULATION"].map(mode => {
                    const modeStats = stats.by_mode?.[mode] || {};
                    return (
                      <Card key={mode} className="bg-white/5 border-white/10">
                        <CardHeader className="pb-2">
                          <CardTitle className={`text-lg ${mode === "LIVE" ? "text-green-400" : "text-purple-400"}`}>
                            {mode}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-white/50 text-xs">Total Entries</p>
                              <p className="text-2xl font-bold text-white">{modeStats.total_entries || 0}</p>
                            </div>
                            <div>
                              <p className="text-white/50 text-xs">Total Hits</p>
                              <p className="text-2xl font-bold text-divine-gold">{modeStats.total_hits || 0}</p>
                            </div>
                            <div>
                              <p className="text-white/50 text-xs">High Severity</p>
                              <p className="text-xl font-bold text-red-400">{modeStats.high_severity_count || 0}</p>
                            </div>
                            <div>
                              <p className="text-white/50 text-xs">Warn Severity</p>
                              <p className="text-xl font-bold text-yellow-400">{modeStats.warn_severity_count || 0}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Top Patterns */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Top Patterns (Last {stats.period_days} Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.top_patterns?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.top_patterns.map((item, index) => (
                          <div 
                            key={item.pattern}
                            className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-white/40 text-sm w-6">{index + 1}.</span>
                              <span className="text-white font-mono">{item.pattern}</span>
                            </div>
                            <span className="text-divine-gold font-bold">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-white/40 text-center py-4">No pattern data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Daily Trend */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-white">Daily Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.daily_trend?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.daily_trend.map(day => (
                          <div 
                            key={day.date}
                            className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                          >
                            <span className="text-white/70">{day.date}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-white/50 text-sm">{day.entries} entries</span>
                              <span className="text-divine-gold font-bold">{day.hits} hits</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-white/40 text-center py-4">No daily data available</p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-12 text-center">
                  <p className="text-white/40">Failed to load statistics</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachLint;
